<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  OverdueAlert,
  EnrolledCourses,
  ContinueLearning,
  UpcomingLive,
  NewOnDemand,
  RecentActivity,
  BrowseLibraries,
  CleAdvisorCard,
  type EnrolledCourse,
  type InProgressCourse,
  type LiveCourse,
  type OnDemandCourse,
} from '@/components/dashboard';
import { type CourseCredit } from '@/components/dashboard/CourseCardRow.vue';
import { JurisdictionFlyout, type JurisdictionCredit } from '@/components/common';
import { useDashboardStore, useUserStore } from '@/stores';
import { useCourseStatus } from '@/composables/useCourseActions';
import { formatDuration, formatCreditLabel } from '@/types/learnerCourse';
import { tileCtaLabel, tileCtaUrl } from '@/utils/dashboardCta';

const dashboardStore = useDashboardStore();
const userStore = useUserStore();

const userName = computed(() => userStore.profile?.firstName || 'Learner');

// Jurisdiction flyout state
const isFlyoutOpen = ref(false);
const flyoutCourseId = ref<string | number | null>(null);
const flyoutCredits = ref<JurisdictionCredit[]>([]);
const flyoutCreditType = ref<string>('CLE');

function handleViewJurisdictions(learningObjectId: string | number, credits: CourseCredit[]) {
  flyoutCourseId.value = learningObjectId;
  flyoutCredits.value = credits.map((c) => ({
    abbreviation: '', // Deprecated - not used in display
    jurisdiction: c.jurisdiction || 'Unknown', // Should be full name (e.g., "New York")
    accredType: (c.credentialCode ?? '').toUpperCase(),
    amount: c.amount ?? (parseFloat(c.label.split(' ').pop() || '1') || 1),
    creditType: c.creditType || 'General',
    // U10: dashboard surfaces "in progress" or "completed" courses, where the
    // learner has already started — by definition the credit is something
    // they're earning. Default to Approved so the chip renders the green
    // pathway. (CourseCredit doesn't carry the resolver-driven status today;
    // upgrading the dashboard endpoint to surface it is out of scope for U10.)
    learnerFacingStatus: 'Approved',
    isUserJurisdiction: c.userMatch ?? false, // Highlight user's saved jurisdictions
  }));
  // Flyout title uses the first credit's credential code — when a dual-credit
  // course is in the list, whichever code comes first wins. Matches the old
  // behavior where the first 'CPE' hit flipped the title.
  const firstCode = credits.find((c) => c.credentialCode)?.credentialCode;
  flyoutCreditType.value = firstCode ? firstCode.toUpperCase() : 'CLE';
  isFlyoutOpen.value = true;
}

function closeFlyout() {
  isFlyoutOpen.value = false;
  flyoutCourseId.value = null;
  flyoutCredits.value = [];
}

// Map API types to component types - include full credit info for flyout
const { courseStatusMap, loadCourseStatus } = useCourseStatus();

const inProgressCourses = computed<InProgressCourse[]>(() =>
  dashboardStore.inProgressCourses.map((c) => ({
    id: c.id,
    title: c.title,
    progress: c.progress ?? 0,
    duration: formatDuration(c.duration),
    practiceAreas: c.practiceAreas,
    credentialCodes: c.credentialCodes,
    learningObjectTypeCode: c.learningObjectTypeCode,
    credits: c.credits?.map((cr) => ({
      label: formatCreditLabel(cr),
      userMatch: cr.userMatch,
      jurisdiction: cr.jurisdiction,
      creditType: cr.creditType,
      amount: cr.amount,
    })),
    // BPE-571: the CTA follows the server's claim verdict, via the same helpers History uses.
    // It used to read `progress === 100 ? 'Claim certificate'` and route straight to the claim
    // page — watch percentage alone, weaker even than the old status-map rule, so a learner who
    // finished the video with no jurisdiction on file was invited to claim a certificate they
    // could never legitimately earn.
    actionLabel: tileCtaLabel(c.id, courseStatusMap.value, c.progress ?? 0, c.learningObjectTypeCode),
    actionUrl: tileCtaUrl(c.id, courseStatusMap.value, c.deliveryMode, c.learningObjectTypeCode),
    deliveryMode: c.deliveryMode,
    assignedAt: c.assignedAt ?? null,
    recordedDate: c.originalLiveDate ?? null,
  }))
);

const liveCourses = computed<LiveCourse[]>(() =>
  dashboardStore.upcomingLiveCourses.map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    dateTime: c.liveDateTime
      ? new Date(c.liveDateTime).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        })
      : '',
    duration: formatDuration(c.duration),
    practiceAreas: c.practiceAreas,
    credentialCodes: c.credentialCodes,
    credits: c.credits?.map((cr) => ({
      label: formatCreditLabel(cr),
      userMatch: cr.userMatch,
      jurisdiction: cr.jurisdiction,
      creditType: cr.creditType,
      amount: cr.amount,
    })),
    liveEventId: c.liveEventId,
    scheduledAtUtc: c.liveDateTime,
    durationMinutes: c.duration,
  }))
);

const newCourses = computed<OnDemandCourse[]>(() =>
  dashboardStore.newOnDemandCourses.map((c) => ({
    id: c.id,
    title: c.title,
    duration: formatDuration(c.duration),
    practiceAreas: c.practiceAreas,
    credentialCodes: c.credentialCodes,
    learningObjectTypeCode: c.learningObjectTypeCode,
    credits: c.credits?.map((cr) => ({
      label: formatCreditLabel(cr),
      userMatch: cr.userMatch,
      jurisdiction: cr.jurisdiction,
      creditType: cr.creditType,
      amount: cr.amount,
    })),
    recordedDate: c.originalLiveDate ?? null,
  }))
);

const enrolledCourses = computed<EnrolledCourse[]>(() =>
  dashboardStore.enrolledWebinars.map((w) => ({
    id: w.id,
    title: w.title,
    description: w.description,
    dateTime: new Date(w.scheduledAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }),
    duration: formatDuration(w.durationMinutes),
    credentialCodes: w.credentialCodes,
    launchUrl: null,
    liveEventId: w.liveEventId,
    scheduledAtUtc: w.scheduledAt,
    durationMinutes: w.durationMinutes,
  }))
);

// Recent activity items come directly from the store (ActivityFeedItem[])
const recentActivityItems = computed(() => dashboardStore.recentActivity);

onMounted(async () => {
  await dashboardStore.fetchDashboard();
  // Claim eligibility is server-computed (BPE-571), so the CTA needs the status map.
  loadCourseStatus(dashboardStore.inProgressCourses.map((c) => Number(c.id)));
});
</script>

<template>
  <div class="content-header">
    <h1>Welcome back, {{ userName }}!</h1>
  </div>

  <div class="content-body">
    <div class="dashboard-layout">
      <CleAdvisorCard v-if="userStore.featureFlags.creditTracker" />
      <OverdueAlert />
      <EnrolledCourses
        :courses="enrolledCourses"
        @view-jurisdictions="handleViewJurisdictions"
      />
      <ContinueLearning
        :courses="inProgressCourses"
        @view-jurisdictions="handleViewJurisdictions"
      />
      <UpcomingLive
        :courses="liveCourses"
        @view-jurisdictions="handleViewJurisdictions"
      />
      <NewOnDemand
        :courses="newCourses"
        @view-jurisdictions="handleViewJurisdictions"
      />
      <RecentActivity :items="recentActivityItems" />
      <BrowseLibraries />
    </div>
  </div>

  <!-- Jurisdiction Credits Flyout -->
  <JurisdictionFlyout
    :is-open="isFlyoutOpen"
    :learning-object-id="flyoutCourseId ?? undefined"
    :credit-type="flyoutCreditType"
    :jurisdictions="flyoutCredits"
    @close="closeFlyout"
  />
</template>
